import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Text } from '@Sage/hoi-poi-ui';
import { normalizeDecimalValue } from 'utils/decimal';

import './styles.scss';

function QuantityItem({
    className,
    onChange,
    quantity,
    max,
    min = 0,
    allowDecimals = false,
    limitDecimals,
}) {
    const rootClassName = classNames('fm-quantity-input', className);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);

    const clampValueByDecimals = useCallback(
        (value) => {
            if (!allowDecimals) return value;

            const parsed = normalizeDecimalValue(value);
            if (parsed == null) return null;

            if (limitDecimals === undefined || limitDecimals === null) {
                return parsed;
            }

            if (limitDecimals <= 0) {
                return Math.round(parsed);
            }

            return Number(parsed.toFixed(limitDecimals));
        },
        [allowDecimals, limitDecimals],
    );

    const insertAtSelection = useCallback(
        (currentValue, insertedValue, selectionStart, selectionEnd) => {
            return (
                currentValue.slice(0, selectionStart) +
                insertedValue +
                currentValue.slice(selectionEnd)
            );
        },
        [],
    );

    const getSelectionOffsets = useCallback((element) => {
        const selection = window.getSelection?.();
        if (!selection || !element || selection.rangeCount === 0) return null;

        const range = selection.getRangeAt(0);
        if (!element.contains(range.startContainer) || !element.contains(range.endContainer)) {
            return null;
        }

        return {
            start: range.startOffset,
            end: range.endOffset,
        };
    }, []);

    const isValidDecimalDraft = useCallback(
        (value) => {
            if (!allowDecimals) return /^\d+$/.test(value);

            const normalized = String(value).replace(',', '.');
            if (normalized === '') return true;
            if (!/^-?\d*(?:\.\d*)?$/.test(normalized)) return false;
            if (/^-?0\.$/.test(normalized)) return false;
            if (limitDecimals === undefined || limitDecimals === null) return true;

            const decimalPart = normalized.split('.')[1] || '';
            return decimalPart.length <= limitDecimals;
        },
        [allowDecimals, limitDecimals],
    );

    const getDisplayValue = useCallback(
        (value) => {
            if (value === null || value === undefined || value === '') return String(min);

            if (!allowDecimals) return String(value);

            const parsed = normalizeDecimalValue(value);
            if (parsed == null) return String(min);

            if (limitDecimals === undefined || limitDecimals === null) {
                return String(parsed);
            }

            if (limitDecimals <= 0) {
                return String(Math.round(parsed));
            }

            return parsed.toFixed(limitDecimals);
        },
        [allowDecimals, limitDecimals, min],
    );

    const parseValue = useCallback(
        (value) => {
            if (allowDecimals) {
                return clampValueByDecimals(value);
            }

            const parsed = parseInt(value, 10);
            return isNaN(parsed) ? null : parsed;
        },
        [allowDecimals, clampValueByDecimals],
    );

    const clampValue = useCallback(
        (value) => {
            if (value == null) return min;
            if (typeof max === 'number' && value > max) return max;
            if (value < min) return min;
            return value;
        },
        [max, min],
    );

    useEffect(() => {
        if (!isFocused && inputRef.current) {
            inputRef.current.innerText = getDisplayValue(quantity);
        }
    }, [getDisplayValue, isFocused, quantity]);

    const handleKeyDown = useCallback(
        (e) => {
            const allowedKeys = [
                48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 96, 97, 98, 99, 100, 101, 102, 103, 104,
                105, 8, 37, 39, 9, 46,
            ];

            if (allowDecimals) {
                allowedKeys.push(188, 190, 110);
            }

            if (!allowedKeys.includes(e.keyCode)) {
                e.preventDefault();
                return;
            }

            if (!allowDecimals) return;

            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.keyCode === 8 || e.keyCode === 46 || e.keyCode === 37 || e.keyCode === 39) {
                return;
            }

            const currentValue = e.currentTarget?.innerText || '';
            const selection = getSelectionOffsets(e.currentTarget);
            const selectionStart = selection?.start ?? currentValue.length;
            const selectionEnd = selection?.end ?? currentValue.length;
            const nextValue = insertAtSelection(currentValue, e.key, selectionStart, selectionEnd);

            if (/^[.,]$/.test(e.key) && /^-?0$/.test(currentValue.trim())) {
                e.preventDefault();
                return;
            }

            if (!isValidDecimalDraft(nextValue)) {
                e.preventDefault();
            }
        },
        [allowDecimals, getSelectionOffsets, insertAtSelection, isValidDecimalDraft],
    );

    const handlePaste = useCallback(
        (e) => {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text');
            const currentValue = e.currentTarget?.innerText || '';
            const selection = getSelectionOffsets(e.currentTarget);
            const selectionStart = selection?.start ?? currentValue.length;
            const selectionEnd = selection?.end ?? currentValue.length;
            const nextValue = insertAtSelection(currentValue, text, selectionStart, selectionEnd);

            if (isValidDecimalDraft(nextValue)) {
                e.currentTarget.innerText = nextValue;
            } else {
                e.preventDefault();
            }
        },
        [getSelectionOffsets, insertAtSelection, isValidDecimalDraft],
    );

    const handleFocus = useCallback(() => {
        setIsFocused(true);
        if (inputRef.current) {
            inputRef.current.innerText = getDisplayValue(quantity);
        }
    }, [getDisplayValue, quantity]);

    const onChangeSpan = useCallback(
        (e) => {
            const nextValue = clampValue(parseValue(e.currentTarget.innerText));
            if (e.currentTarget) {
                e.currentTarget.innerText = String(nextValue);
            }
            setIsFocused(false);
            onChange(nextValue);
        },
        [clampValue, onChange, parseValue],
    );

    const addQuantity = useCallback(
        () => onChange(clampValue(quantity + 1)),
        [clampValue, onChange, quantity],
    );

    const subQuantity = useCallback(
        () => onChange(clampValue(quantity - 1)),
        [clampValue, onChange, quantity],
    );

    return (
        <div className={rootClassName}>
            <Text
                className={classNames('fm-quantity-input__left-action', {
                    disabled: quantity === min,
                })}
                onClick={subQuantity}
            >
                -
            </Text>
            <span
                ref={inputRef}
                className="fm-quantity-input__input"
                onBlur={onChangeSpan}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                contentEditable
                suppressContentEditableWarning
            >
                {getDisplayValue(quantity)}
            </span>
            <Text
                className={classNames('fm-quantity-input__right-action', {
                    disabled: max && quantity === max,
                })}
                onClick={addQuantity}
            >
                +
            </Text>
        </div>
    );
}

QuantityItem.propTypes = {
    allowDecimals: PropTypes.bool,
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    quantity: PropTypes.number.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
};

export default memo(QuantityItem);
